import { UserInfo } from 'types/entities/UserInfo';
import { ISharedEquipmentEntry, ISharedUser } from 'types/IEquipmentEntry';
import { SharedItem } from 'services/api/shares';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';

/**
 * Groups shared users from equipment list, filtering by user permissions
 * and organizing shared equipment and fleets by user.
 */
export function groupSharedUsers(
  equipmentList: ISharedEquipmentEntry[],
  me: UserInfo,
): ISharedUser[] {
  const userMap = new Map<string, ISharedUser>();

  for (const equipment of equipmentList) {
    if (!canManageShares(equipment)) continue;

    const shareContext = getShareContext(equipment);
    if (!shareContext) continue;

    processSharedUsers(shareContext, me, userMap);
  }

  return Array.from(userMap.values()).sort(sortByDisplayName);
}

/**
 * Checks if the current user has permission to manage shares for the equipment
 */
function canManageShares(equipment: ISharedEquipmentEntry): boolean {
  const myRole = getUserRole(equipment);
  return myRole === 'OWNER' || myRole === 'ADMIN';
}

/**
 * Gets the user's role for the given equipment
 */
function getUserRole(
  equipment: ISharedEquipmentEntry,
): AccessSharingRole | undefined {
  const isFleetShare = Boolean(equipment.isSharedAsPartOfFleet);
  return isFleetShare
    ? equipment.fleetData?.sharedItemDetails.myRole
    : equipment.shareData?.sharedItemDetails.myRole;
}

/**
 * Extracts share context (shared data and users) from equipment
 */
function getShareContext(equipment: ISharedEquipmentEntry) {
  const isFleetShare = Boolean(equipment.isSharedAsPartOfFleet);
  const shareData = isFleetShare ? equipment.fleetData : equipment.shareData;

  if (!shareData) return null;

  return {
    shareData,
    isFleetShare,
    sharedWith: shareData.sharedWith || [],
  };
}

/**
 * Processes shared users and adds them to the user map
 */
function processSharedUsers(
  shareContext: {
    shareData: SharedItem;
    isFleetShare: boolean;
    sharedWith: any[];
  },
  me: UserInfo,
  userMap: Map<string, ISharedUser>,
): void {
  const { shareData, isFleetShare, sharedWith } = shareContext;

  for (const user of sharedWith) {
    if (user.email === me.email) continue;

    const existingUser = userMap.get(user.email);
    if (existingUser) {
      addShareToUser(existingUser, shareData, isFleetShare);
    } else {
      const newUser = createSharedUser(user, shareData, isFleetShare);
      userMap.set(user.email, newUser);
    }
  }
}

/**
 * Creates a new shared user object
 */
function createSharedUser(
  user: any,
  shareData: SharedItem,
  isFleetShare: boolean,
): ISharedUser {
  const sharedUser: ISharedUser = {
    email: user.email,
    displayName: user.displayName,
    role: user.sharedRole,
    sharedEquipments: [],
    sharedFleets: [],
  };

  addShareToUser(sharedUser, shareData, isFleetShare);
  return sharedUser;
}

/**
 * Adds share data to the appropriate array (equipment or fleet)
 */
function addShareToUser(
  user: ISharedUser,
  shareData: SharedItem,
  isFleetShare: boolean,
): void {
  if (isFleetShare) {
    user.sharedFleets.push(shareData);
  } else {
    user.sharedEquipments.push(shareData);
  }
}

/**
 * Sorts users by display name
 */
function sortByDisplayName(a: ISharedUser, b: ISharedUser): number {
  return a.displayName.localeCompare(b.displayName);
}
